import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import '../styles/landing.css';

const WORKFLOW_STEPS = [
  {
    num: '01',
    icon: '📥',
    title: 'Import Customers',
    desc: 'Upload a CSV or connect your system. One-time setup, takes 2 minutes.',
    detail: 'customer_data.csv uploaded — 847 customers synced',
  },
  {
    num: '02',
    icon: '⭐',
    title: 'Collect Reviews',
    desc: 'A friendly review request follows, boosting your Google rating automatically.',
    detail: '⭐⭐⭐⭐⭐ "Love this place!" — new Google review',
  },
  {
    num: '03',
    icon: '🔔',
    title: 'Smart Reminders',
    desc: 'When it\'s been a while, we nudge them back with an email or SMS reminder.',
    detail: '"Time for a touch-up, Sarah? Book now →" — reminder sent',
  },
  {
    num: '04',
    icon: '🔁',
    title: 'Customer Returns',
    desc: 'They click, they book, they come back. Repeat. Revenue grows on autopilot.',
    detail: 'Sarah Johnson booked — 3rd visit this quarter',
  },
];

const TESTIMONIALS = [
  {
    name: 'Jessica M.',
    role: 'Owner, Luxe Hair Studio',
    avatar: 'JM',
    color: '#6C5CE7',
    text: 'We went from 30% repeat rate to over 60% in just two months. LoopVisitor is the best investment I\'ve made for my salon. The automated messages feel personal — my clients think I\'m sending them individually!',
    stars: 5,
  },
  {
    name: 'Marcus T.',
    role: 'Manager, Fresh Cuts Barbershop',
    avatar: 'MT',
    color: '#00B894',
    text: 'I used to spend hours texting clients reminders. Now it\'s fully automated and my Google reviews went from 23 to 180+ in four months. The ROI is insane.',
    stars: 5,
  },
  {
    name: 'Priya K.',
    role: 'Owner, Zen Day Spa',
    avatar: 'PK',
    color: '#E17055',
    text: 'My clients love the follow-up messages. Three of them told me how "thoughtful" I was. Little do they know it\'s all automated! LoopVisitor pays for itself 10x over.',
    stars: 5,
  },
  {
    name: 'David R.',
    role: 'Owner, Sparkle Auto Wash',
    avatar: 'DR',
    color: '#0984E3',
    text: 'We track everything through the booking link clicks. Knowing which channel drives more returns helps us double down on what works. Absolute game changer.',
    stars: 5,
  },
];

const INDUSTRIES = [
  { icon: '💇‍♀️', label: 'Salons' },
  { icon: '💈', label: 'Barbershops' },
  { icon: '🧖‍♀️', label: 'Spas' },
  { icon: '🚗', label: 'Car Washes' },
  { icon: '🦷', label: 'Dental Clinics' },
  { icon: '🏋️', label: 'Fitness Studios' },
  { icon: '🍽️', label: 'Restaurants' },
  { icon: '🐾', label: 'Pet Groomers' },
  { icon: '🔬', label: 'Pathology Labs' },
];

function useInView() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

function AnimatedCounter({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const [ref, visible] = useInView();

  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const duration = 2000;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [visible, target]);

  return <span ref={ref}>{count}{suffix}</span>;
}

function WorkflowTimeline() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % WORKFLOW_STEPS.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="workflow-container">
      <div className="workflow-left">
        {WORKFLOW_STEPS.map((step, i) => (
          <button
            key={i}
            className={`workflow-step ${activeStep === i ? 'active' : ''} ${i < activeStep ? 'done' : ''}`}
            onClick={() => setActiveStep(i)}
          >
            <div className="workflow-step-line">
              <div className="workflow-dot">
                {i < activeStep ? '✓' : step.num}
              </div>
              {i < WORKFLOW_STEPS.length - 1 && (
                <div className="workflow-connector">
                  <div
                    className="workflow-connector-fill"
                    style={{ height: i < activeStep ? '100%' : i === activeStep ? '50%' : '0%' }}
                  />
                </div>
              )}
            </div>
            <div className="workflow-step-content">
              <h4>{step.title}</h4>
              <p>{step.desc}</p>
            </div>
          </button>
        ))}
      </div>
      <div className="workflow-right">
        <div className="workflow-preview">
          <div className="preview-header">
            <div className="preview-dots">
              <span /><span /><span />
            </div>
            <span className="preview-title">LoopVisitor</span>
          </div>
          <div className="preview-body">
            <div className="preview-icon" key={activeStep}>
              {WORKFLOW_STEPS[activeStep].icon}
            </div>
            <h3 className="preview-heading" key={`h-${activeStep}`}>
              {WORKFLOW_STEPS[activeStep].title}
            </h3>
            <div className="preview-notification" key={`n-${activeStep}`}>
              <div className="notif-dot" />
              <span>{WORKFLOW_STEPS[activeStep].detail}</span>
            </div>
            <div className="preview-progress">
              <div className="preview-progress-bar">
                <div
                  className="preview-progress-fill"
                  style={{ width: `${((activeStep + 1) / WORKFLOW_STEPS.length) * 100}%` }}
                />
              </div>
              <span className="preview-progress-label">
                Step {activeStep + 1} of {WORKFLOW_STEPS.length}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PricingSection() {
  const [plans, setPlans] = useState(null);
  const [currency, setCurrency] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [ref, visible] = useInView();

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(false);
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    try {
      const res = await fetch(`${API_BASE}/razorpay/public-plans`, {
        headers: { 'X-Timezone': tz },
      });
      if (res.ok) {
        const data = await res.json();
        setPlans(data.plans);
        setCurrency(data.currency || 'USD');
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  return (
    <section className="section-pricing" id="pricing" ref={ref}>
      <div className="section-container">
        <div className="section-label">Pricing</div>
        <h2 className="section-title">Simple, transparent pricing</h2>
        <p className="section-subtitle">
          {plans
            ? `Start free, upgrade as you grow. ${currency === 'INR' ? 'Prices shown in INR.' : 'Prices shown in USD.'}`
            : 'Start free, upgrade as you grow.'}
        </p>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="pricing-loading-spinner" />
            <p style={{ color: '#64748B', marginTop: 16 }}>Loading plans...</p>
          </div>
        )}

        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p style={{ color: '#64748B', marginBottom: 16 }}>Could not load pricing plans.</p>
            <button onClick={fetchPlans} className="btn btn-secondary">
              Try Again
            </button>
          </div>
        )}

        {plans && !loading && (
          <>
            <div className={`pricing-landing-grid ${visible ? 'visible' : ''}`}>
              {plans.filter(plan => ['free', 'starter', 'growth'].includes(plan.id)).map((plan, i) => {
                const isGrowth = plan.id === 'growth';
                const isFree = plan.id === 'free';
                const priceDisplay = plan.displayPrice.replace('/month', '');
                return (
                  <div
                    key={plan.id}
                    className={`pricing-landing-card ${isGrowth ? 'highlighted' : ''}`}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    {isGrowth && <div className="pricing-popular-badge">Most Popular</div>}
                    <h3>{plan.name}</h3>
                    <div className="pricing-landing-price">
                      {priceDisplay}
                      {!isFree && <span>/month</span>}
                    </div>
                    <ul className="pricing-landing-features">
                      {plan.features.map((f, j) => (
                        <li key={j}>{f}</li>
                      ))}
                    </ul>
                    <Link
                      to="/signup"
                      className={`btn ${isGrowth ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ width: '100%', marginTop: 'auto' }}
                    >
                      {isFree ? 'Start Free' : 'Get Started'}
                    </Link>
                  </div>
                );
              })}
            </div>

            <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#64748B' }}>
              Set up in 5 minutes · Cancel anytime
            </p>
          </>
        )}
      </div>
    </section>
  );
}

export default function Landing() {
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const [statsRef, statsVisible] = useInView();
  const [featRef, featVisible] = useInView();
  const [testRef, testVisible] = useInView();
  const [videoRef, videoVisible] = useInView();

  return (
    <div className="landing">
      {/* Nav */}
      <nav className={`landing-nav ${navScrolled ? 'scrolled' : ''}`}>
        <div className="landing-nav-inner">
          <BrandLogo className="landing-logo" />
          <button
            className="landing-mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
          <div className={`landing-nav-links ${mobileMenuOpen ? 'open' : ''}`}>
            <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)}>How it Works</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="#testimonials" onClick={() => setMobileMenuOpen(false)}>Testimonials</a>
            <Link to="/book-demo" className="nav-link-login" onClick={() => setMobileMenuOpen(false)}>Book Demo</Link>
            <Link to="/login" className="nav-link-login" onClick={() => setMobileMenuOpen(false)}>Log in</Link>
            <Link to="/signup" className="btn btn-primary btn-nav" onClick={() => setMobileMenuOpen(false)}>Start Free</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-bg-orb hero-orb-1" />
        <div className="hero-bg-orb hero-orb-2" />
        <div className="hero-bg-orb hero-orb-3" />

        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            Trusted by 500+ local businesses
          </div>

          <h1>
            Get more repeat customers<br />
            <span className="hero-gradient">on autopilot</span>
          </h1>

          <p className="hero-sub">
            LoopVisitor automatically follows up with your clients, gets you more reviews,
            and brings them back — without extra work.
          </p>

          <div className="hero-actions">
            <Link to="/signup" className="btn btn-primary btn-lg">
              Start Free Trial
              <span className="btn-arrow">→</span>
            </Link>
            <Link to="/book-demo" className="btn btn-ghost btn-lg">
              Book a Demo
            </Link>
          </div>

          <p className="hero-note">Set up in 5 minutes · Cancel anytime</p>

          <div className="hero-industries">
            {INDUSTRIES.map((ind, i) => (
              <div key={i} className="hero-industry" style={{ animationDelay: `${i * 0.1}s` }}>
                <span>{ind.icon}</span>
                <span>{ind.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem stat */}
      <section className="problem-section" ref={statsRef}>
        <div className={`problem-inner ${statsVisible ? 'visible' : ''}`}>
          <div className="problem-stat-card">
            <div className="problem-number">
              <AnimatedCounter target={40} suffix="%" />
            </div>
            <p>of customers never come back after their first visit</p>
          </div>
          <div className="problem-divider" />
          <div className="problem-text">
            <h2>Most businesses lose 20–40% of customers after their first visit.</h2>
            <p className="problem-fix">
              <strong>We fix that automatically.</strong> LoopVisitor turns one-time visitors into loyal regulars
              with zero effort from you.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section-hiw" id="how-it-works">
        <div className="section-container">
          <div className="section-label">How It Works</div>
          <h2 className="section-title">Set it once. Forget it. Watch customers return.</h2>
          <p className="section-subtitle">
            Super simple — we connect to your system or import your customer data once.
            After that, everything runs automatically in the background.
          </p>

          <WorkflowTimeline />
        </div>
      </section>

      {/* Autopilot features */}
      <section className="section-features" ref={featRef}>
        <div className="section-container">
          <div className="section-label">Set It & Forget It</div>
          <h2 className="section-title">Everything runs in the background</h2>
          <p className="section-subtitle">
            You don't have to touch anything. Here's what happens automatically:
          </p>

          <div className={`features-grid ${featVisible ? 'visible' : ''}`}>
            <div className="feature-card">
              <div className="feature-icon-wrap" style={{ background: 'rgba(16,185,129,0.1)' }}>🔔</div>
              <h3>Reminders Are Scheduled</h3>
              <p>Smart reminders nudge customers to rebook when it's been too long since their last visit.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrap" style={{ background: 'rgba(245,158,11,0.1)' }}>⭐</div>
              <h3>Reviews Are Collected</h3>
              <p>Automated review requests boost your Google rating and attract new customers organically.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrap" style={{ background: 'rgba(108,92,231,0.1)' }}>✨</div>
              <h3>AI-Powered Templates</h3>
              <p>One-click AI improves your email and SMS messages — always personal, never generic.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-wrap" style={{ background: 'rgba(59,130,246,0.1)' }}>📊</div>
              <h3>Track What Works</h3>
              <p>See exactly how many customers clicked your booking link from email and SMS in real-time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      {/* Testimonials */}
      <section className="section-testimonials" id="testimonials" ref={testRef}>
        <div className="section-container">
          <div className="section-label">Customer Love</div>
          <h2 className="section-title">Don't just take our word for it</h2>
          <p className="section-subtitle">
            Hear from business owners who transformed their customer retention.
          </p>

          <div className={`testimonials-grid ${testVisible ? 'visible' : ''}`}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="testimonial-card" style={{ animationDelay: `${i * 0.15}s` }}>
                <div className="testimonial-stars">
                  {'★'.repeat(t.stars)}
                </div>
                <p className="testimonial-text">"{t.text}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar" style={{ background: t.color }}>
                    {t.avatar}
                  </div>
                  <div>
                    <div className="testimonial-name">{t.name}</div>
                    <div className="testimonial-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Tutorial */}
      {/* <section className="section-video" id="video-tutorial" ref={videoRef}>
        <div className="section-container">
          <div className="section-label">Watch & Learn</div>
          <h2 className="section-title">See LoopVisitor in action</h2>
          <p className="section-subtitle">
            Watch a quick walkthrough to see how easy it is to set up and start growing your business.
          </p>
          <div className={`video-wrapper ${videoVisible ? 'visible' : ''}`}>
            <div className="video-container">
              <iframe
                src={process.env.REACT_APP_YOUTUBE_TUTORIAL_URL || 'https://www.youtube.com/embed/dQw4w9WgXcQ'}
                title="LoopVisitor Tutorial"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section> */}

      {/* Support / Help Section */}
      <section className="section-support" id="support">
        <div className="section-container">
          <div className="section-label">Help & Support</div>
          <h2 className="section-title">Need help? We're here for you</h2>
          <p className="section-subtitle">
            Whether you're getting started or need technical support, our team is ready to assist.
          </p>
          <div className="support-grid">
            <div className="support-card">
              <div className="support-icon">📧</div>
              <h3>Email Support</h3>
              <p>Get help via email. We typically respond within 24 hours.</p>
              <Link to="/support" className="btn btn-secondary btn-sm">
                Raise a Ticket
              </Link>
            </div>
            <div className="support-card">
              <div className="support-icon">📅</div>
              <h3>Book a Demo</h3>
              <p>Get a personalized walkthrough of all LoopVisitor features.</p>
              <Link to="/book-demo" className="btn btn-secondary btn-sm">
                Schedule Demo
              </Link>
            </div>
            <div className="support-card">
              <div className="support-icon">📖</div>
              <h3>Getting Started</h3>
              <p>Create a free account and follow the simple setup wizard.</p>
              <Link to="/signup" className="btn btn-primary btn-sm">
                Create Free Account
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-cta">
        <div className="cta-inner">
          <div className="cta-bg-glow" />
          <h2>Stop losing customers.<br />Start growing on autopilot.</h2>
          <p>Join 500+ businesses that bring their customers back automatically.</p>
          <div className="hero-actions" style={{ justifyContent: 'center' }}>
            <Link to="/signup" className="btn btn-primary btn-lg">
              Create Account for Free
              <span className="btn-arrow">→</span>
            </Link>
            <Link to="/support" className="btn btn-ghost btn-lg">
              Raise Support Ticket
            </Link>
          </div>
          <p className="hero-note" style={{ marginTop: 16 }}>
            Set up in 5 minutes · Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="footer-brand">
            <BrandLogo className="landing-logo" />
            <p>Automatically bring your customers back.</p>
          </div>
          <div className="footer-links">
            <div>
              <h4>Product</h4>
              <a href="#how-it-works">How It Works</a>
              <a href="#pricing">Pricing</a>
              <Link to="/signup">Get Started</Link>
              <Link to="/login">Log In</Link>
            </div>
            <div>
              <h4>Industries</h4>
              <span>Salons & Spas</span>
              <span>Barbershops</span>
              <span>Car Washes</span>
              <span>Restaurants</span>
            </div>
            <div>
              <h4>Support</h4>
              <Link to="/book-demo">Book a Demo</Link>
              <Link to="/support">Raise a Ticket</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Service</Link>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} LoopVisitor. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
